import idx from 'idx';

import { DefaultMentionNameResolver, PresenceResource } from '@atlaskit/mention';

import { CONTEXT_PATH } from '@confluence/named-routes';
import { getTeamUrl } from '@confluence/profile';
import { getExternalCollaboratorEntitlementFromQuery } from '@confluence/external-collab-ui';
import {
	UserTeamMentionProvider,
	fetchMentionsProviderData,
} from '@confluence/fabric-media-support';

import { MentionNameClient } from './mention-name-client';

const USER_MENTION_SERVICE_URL = '/gateway/api/mentions';
const TEAM_MENTION_SERVICE_URL = '/gateway/api/teams/mentions';

async function createFabricMentionResource(cloudId, spaceId, accountId, withAnalyticsEventProps) {
	const userMentionConfig = {
		url: `${USER_MENTION_SERVICE_URL}/${cloudId}`,
		productId: 'micros-group/confluence',
		containerId: spaceId || null,
		shouldHighlightMention: (mention) => mention.id === accountId,
	};

	const teamMentionConfig = {
		...userMentionConfig,
		url: TEAM_MENTION_SERVICE_URL,
		teamLinkResolver: (teamId) => getTeamUrl(teamId, 'fabric', CONTEXT_PATH),
	};

	userMentionConfig.mentionNameResolver = new DefaultMentionNameResolver(
		new MentionNameClient(),
		withAnalyticsEventProps,
	);

	return userTeamMentionProvider(userMentionConfig, teamMentionConfig);
}

async function userTeamMentionProvider(userMentionConfig, teamMentionConfig) {
	const data = await fetchMentionsProviderData();

	// Entitlement check to determine if tenant is premium/enabled for Guests
	const includeExternalCollaborators = getExternalCollaboratorEntitlementFromQuery(data) === true;

	return new UserTeamMentionProvider(
		'/gateway/api/v1/recommendations',
		userMentionConfig,
		teamMentionConfig,
		includeExternalCollaborators,
	);
}

export default async function getMentionsConfig(
	cloudId,
	accountId,
	mentionContext,
	withAnalyticsEventProps,
) {
	const spaceId = idx(mentionContext, (_) => _.spaceId) || null;
	if (!cloudId) {
		throw new Error('MentionResource cannot be configured without a cloud id');
	}

	const mentionResource = await createFabricMentionResource(
		cloudId,
		spaceId,
		accountId,
		withAnalyticsEventProps,
	);

	return {
		mentionResource,
		presenceResource: new PresenceResource({
			url: '/gateway/api/directory/graphql',
			cloudId,
			productId: 'confluence',
		}),
		// Required for the TinyMCE mentions plugin, should be removed when TinyMCE is removed
		mentionServiceUrl: `${USER_MENTION_SERVICE_URL}/${cloudId}`,
		contextIdentifier: {
			containerId: spaceId,
			objectId: idx(mentionContext, (_) => _.contentId) || null,
			childObjectId: idx(mentionContext, (_) => _.commentId) || null,
		},
	};
}
